export type ModuleId = 'tasks' | 'completed' | 'groceries' | 'stock' | 'meals' | 'health' | 'fyp' | 'states' | 'goals' | 'voice' | 'agents' | 'it' | 'boardgames';

export const DEFAULT_MODULE_ORDER: ModuleId[] = ['fyp', 'tasks', 'completed', 'groceries', 'stock', 'meals', 'health', 'states', 'goals', 'voice', 'agents', 'it', 'boardgames'];

/** Clan niche: determines default face + module defaults. */
export type ClanNiche = 'home' | 'work';

/**
 * Work-clan module defaults: tasks/completed/states/goals/cockpit ON,
 * meals/groceries/stock/health/fyp/voice OFF.
 * Home-clan: everything on (existing behavior unchanged).
 */
export const WORK_NICHE_MODULE_DEFAULTS: Partial<Record<ModuleId, boolean>> = {
  fyp: false,
  tasks: true,
  completed: true,
  groceries: false,
  stock: false,
  meals: false,
  health: false,
  states: true,
  goals: true,
  voice: false,
  agents: true,
  it: true,
  boardgames: false,
};

import type { TagIconId, TagType } from '../types/tag.js';
import type { TaskListSectionEntry } from '../types/taskListLayout.js';

export type TagIconMap = Partial<Record<TagType, TagIconId>>;

export interface Team {
  id: string;
  name: string;
  slug: string | null;
  slogan: string | null;
  imageUrl: string | null;
  createdAt: Date;
  createdByUserId: string | null;
  // Weekend window settings (when weekly "weekend" tasks can be completed)
  weekendStartDay: number; // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
  weekendStartHour: number; // 0-23
  weekendEndDay: number; // 0=Sun, 1=Mon, etc.
  weekendEndHour: number; // 0-23 (0 = midnight)
  // Team-wide time of day settings
  morningHour: number; // Default 6 = 06:00
  eveningHour: number; // Default 18 = 18:00
  // Module visibility settings
  showFypModule?: boolean; // Default false/true
  showTasksModule: boolean; // Default true
  showCompletedModule: boolean; // Default true
  showGroceryModule: boolean; // Default true
  showStockModule: boolean; // Default true
  showMealsModule?: boolean; // Default true
  showHealthModule?: boolean; // Default true
  showGoalsModule?: boolean; // Default false
  showVoiceModule?: boolean; // Default true
  showAgentsModule?: boolean; // Default false (ON for work niche)
  showItModule?: boolean; // Default false (ON for work niche)
  showBoardgamesModule?: boolean; // Default true (OFF for work niche)
  moduleOrder: ModuleId[]; // Order of modules in navigation
  moduleLabels?: Partial<Record<ModuleId, string>>;
  tagIcons?: TagIconMap;
  language?: 'nl' | 'en';
  // Theme settings
  themePrimaryColor?: string;
  actionButtonBgColor?: string;
  actionButtonIconColor?: string;
  // Visibility
  isPublic?: boolean;
  // Invite code
  inviteCode?: string | null;
  inviteCodeEnabled?: boolean;
  /** Null = use DEFAULT_TASK_LIST_LAYOUT */
  taskListLayout?: TaskListSectionEntry[] | null;
  /** Team-level flag: when true this team appears in the Freelancer shell's
   * team switcher. Set by team admins; shared across all members & browsers.
   * If no team in the user's set has it on, the shell shows all teams. */
  freelancerEnabled?: boolean;
  /** Clan niche. Stored in localStorage (no DB schema change).
   * Default: 'home' (all existing behavior preserved). */
  niche?: ClanNiche;
}
